import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';

const SongReleasesSetup = () => {
  const navigate = useNavigate();
  const [spotifyConnected, setSpotifyConnected] = useState(false);

  useEffect(() => {
    // Check if Spotify was connected during onboarding
    try {
      const user = JSON.parse(localStorage.getItem('dizeeUser') || '{}');
      const connectorStatuses = JSON.parse(localStorage.getItem('connectorStatuses') || '{}');
      setSpotifyConnected(!!(user?.social?.sf || connectorStatuses?.spotify));
    } catch (error) {
      console.error('Error checking Spotify connection:', error);
    }
  }, []);

  const setupOptions = [
    {
      id: 'spotify-import',
      title: 'Import from Spotify',
      subtitle: '(Fast Fill Catalog)',
      description: 'Quickly pull in releases from Spotify. Fast setup with lighter detail. Includes the basics: artists, artwork, duration, ISRC but no additional metadata or pub info.',
      note: 'This is typically already completed via initial onboarding.',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1DB954">
          <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
        </svg>
      ),
      iconBg: 'rgba(29, 185, 84, 0.15)',
      completed: spotifyConnected,
      action: () => navigate('/connectors/spotify')
    },
    {
      id: 'distributor-upload',
      title: 'Upload distributor or label data',
      subtitle: '(Complete Master Side)',
      description: 'Complete the Master side by importing a CSV file of your full release history directly from your distributor or label. This provides the most complete picture of the master side of your recordings beyond what Spotify can.',
      icon: (
        <svg className="w-5 h-5 text-[#00CED1]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
        </svg>
      ),
      iconBg: 'rgba(0, 206, 209, 0.15)',
      action: () => navigate('/catalog/bulk-import')
    },
    {
      id: 'auto-flow',
      title: 'Setup Auto Flow of Catalog data',
      subtitle: '(Daily Fetching)',
      description: 'As you create new songs, use Dizee Sheets to keep your catalog up to date. Simply convert your existing release spreadsheet into a Dizee Sheet and continue to use Google Sheets how you normally would and watch the new data flow in and get organized in Dizee in real time.',
      icon: (
        <svg className="w-5 h-5 text-[#FF6B35]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" />
        </svg>
      ),
      iconBg: 'rgba(255, 107, 53, 0.15)',
      action: () => navigate('/connectors/google-sheets')
    },
    {
      id: 'manual-add',
      title: 'Manually add new data via DIZEE AI or Catalog',
      subtitle: '(Alternative option)',
      description: "If you don't have any data in a spreadsheet and don't plan to use Dizee Sheets then you can also add releases one at a time by simply hitting the AI Chat or opening the Catalog app and clicking 'Manual'.",
      icon: (
        <svg className="w-5 h-5 text-[#A78BFA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
        </svg>
      ),
      iconBg: 'rgba(167, 139, 250, 0.15)',
      action: () => navigate('/catalog/manual-add')
    }
  ];

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Song Releases Setup</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={() => navigate('/data')}
            >
              Go back
            </span>
          </div>

          <div className="w-full px-4 pb-20 overflow-y-auto flex-1">
            {/* Page Title */}
            <div className="mb-2">
              <h1 className="text-white text-[20px] font-semibold">Song Releases Setup</h1>
              <p className="text-[rgba(255,255,255,0.5)] text-[13px]">(Master Side)</p>
            </div>

            {/* Description */}
            <div className="mb-8">
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] leading-[1.6]">
                Pull your Release History and Writing Credits into Dizee to complete a full picture of your catalog that you can always have in one place for easy access.
              </p>
            </div>

            {/* How it works label */}
            <div className="mb-4">
              <p className="text-[rgba(255,255,255,0.4)] text-[10px] uppercase tracking-wider">How it works</p>
            </div>

            {/* Setup Options */}
            <div className="space-y-4">
              {setupOptions.map((option, index) => (
                <div
                  key={option.id}
                  onClick={option.action}
                  className={`w-full bg-[rgba(255,255,255,0.03)] border rounded-xl p-4 cursor-pointer transition-all duration-200 ${
                    option.completed 
                      ? 'border-green-500 border-opacity-40 hover:border-opacity-60' 
                      : 'border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.25)] hover:bg-[rgba(255,255,255,0.05)]'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon with number badge */}
                    <div className="relative flex-shrink-0">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center border border-[rgba(255,255,255,0.1)]"
                        style={{ backgroundColor: option.iconBg }}
                      >
                        {option.completed ? (
                          <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          option.icon
                        )}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white text-[13px] font-medium">
                          {option.title}
                        </h3>
                        {option.completed && (
                          <span className="text-green-500 text-[10px] bg-green-500 bg-opacity-15 px-2 py-0.5 rounded-full">
                            Completed
                          </span>
                        )}
                      </div>
                      {option.subtitle && (
                        <p className="text-[rgba(255,255,255,0.5)] text-[11px] mb-2">
                          {option.subtitle}
                        </p>
                      )}
                      <p className="text-[rgba(255,255,255,0.55)] text-[11px] leading-[1.5]">
                        {option.description}
                      </p>
                      {option.note && (
                        <p className="text-[rgba(255,255,255,0.4)] text-[10px] mt-2 italic">
                          {option.note}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default SongReleasesSetup;

